/**
 * The tenant dashboard's own footer (COL-533).
 *
 * The design closes every dashboard frame with a dark band carrying one CTA
 * and the tenant's own legal links. Until now the site-wide marketing footer
 * rendered here instead — public sitemap, socials, a "book a viewing" CTA and
 * a second language switcher — under a page whose whole point is that the
 * reader already lives in one of the flats.
 *
 * Two variants, as in the export: an applicant with no booking is still
 * looking for a room, a tenant with one wants to report a problem.
 */

import { Link } from "react-router-dom";

import TenantLegalFooter from "@/components/tenant/TenantLegalFooter";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";

type Props = {
  /**
   * Which frame the dashboard is showing, NOT whether a booking row exists.
   *
   * An applicant has a `bookings` row too — status `pending` — so keying the
   * CTA on "has a booking" offered someone still waiting to hear back a
   * "report an issue" button for a tenancy they do not have yet. The tab set
   * is what decides, and the footer follows it.
   */
  variant: "tenant" | "applicant";
  /** Opens the Help tab — the issue form lives there, not on a separate page. */
  onReportIssue: () => void;
};

export default function TenantDashboardFooter({
  variant,
  onReportIssue,
}: Props) {
  const isTenant = variant === "tenant";
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  return (
    /*
      Full-bleed, as the design has it.

      This was a rounded card sitting inside the page's content column. The
      export's footer spans the whole frame edge to edge, flush, with the same
      horizontal rhythm as the content above it — so the band breaks out of
      the container with a symmetric negative margin and pads itself back in.
    */
    /*
      No negative margins: the shell mounts this OUTSIDE its padded, centred
      container, so the band is already full width, and its own inner wrapper
      lines the content up with the page above.

      It used to break out with `-mx-[calc((100vw-min(100vw,64rem))/2+1rem)]`
      — 64rem and 1rem baked in from the old max-w-5xl/px-4 shell. This PR
      widens that shell, which left the breakout over-wide and hung the
      footer off both edges. A 100vw breakout is fragile regardless: 100vw
      includes the scrollbar, so it overflows by that much whenever one is
      present.
    */
    <footer className="mt-12 w-full bg-foreground py-10 text-background">
      <div className="mx-auto w-full max-w-screen-2xl px-6 lg:px-14">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {isTenant ? d.footerTenantTitle : d.footerApplicantTitle}
          </h2>
          <div className="flex flex-wrap gap-3">
            {isTenant ? (
              <Button variant="secondary" onClick={onReportIssue}>
                {d.footerTenantAction}
              </Button>
            ) : (
              <Button variant="secondary" asChild>
                <Link to="/properties">{d.footerApplicantAction}</Link>
              </Button>
            )}
            <Button
              variant="outline"
              asChild
              className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
            >
              <Link to="/contact">{d.footerWriteUs}</Link>
            </Button>
          </div>
        </div>

        <TenantLegalFooter />
      </div>
    </footer>
  );
}
